<template>
  <div v-if="myActivities.length > 0" class="my-activities">
    <h2>
      <font-awesome-icon :icon="actionIcon" />
      <slot />
      ({{ myActivities.length }})
    </h2>
    <div class="activities-container">
      <div
        v-for="activity in myActivities"
        :key="activity.id"
        class="activity-container"
      >
        <rota-activity-card
          :activity="activity"
          :hide-self-shifts="true"
          :retry="retry"
          @click.prevent="onClick(activity)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import get from "lodash/get";
import has from "lodash/has";
import { Hub } from "aws-amplify/utils";
import {
  isAfter,
  isBefore,
  isToday,
  startOfDay,
  endOfDay,
  subDays,
} from "date-fns";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import { filterActivitiesForUserId } from "@/lib/rota/activity-functions";
import RotaActivityCard from "@/components/activity/RotaActivityCard.vue";
import usePopupActivity from "./usePopupActivity";

export default {
  components: {
    RotaActivityCard,
  },
  props: {
    started: {
      type: Boolean,
      default: false,
    },
    today: {
      type: Boolean,
      default: false,
    },
    retry: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const store = useStore();
    const authStore = useAuthStore();
    const activities = ref(false);
    const { showViewActivityPopup } = usePopupActivity();

    const unableToStartEvents = ["UNAVAILABLE", "REFUSED"];

    const myActivities = computed(() => {
      if (props.retry) {
        // the api call `rotaactivities/getActivitiesWithRetry` returns pre-filtered activities
        return activities.value;
      }

      if (activities.value === false) {
        return [];
      }
      const filtered = filterActivitiesForUserId(
        authStore.isAgencyUser ? authStore.user.agencyId : authStore.user.id,
        activities.value,
      );

      // only show things that have not been finished & haven't been attempted
      return filtered.filter((activity) => {
        const hasStarted =
          has(activity, "start.date", false) ||
          has(activity, "actualStartDate", false);
        const hasFinished =
          has(activity, "stop.date", false) ||
          has(activity, "actualEndDate", false);

        const hasUnableToStart =
          activity.clients?.some((client) =>
            client.reports?.some((report) =>
              unableToStartEvents.includes(report.event),
            ),
          ) ?? false;

        // if today, filter out things that have finished or unable to start
        if (props.today) {
          return (
            !hasFinished && !hasUnableToStart && props.started === hasStarted
          );
        }

        // else, show activities if at least one client's last report isn't "unable to start"
        const hasClientWithoutUnableToStart = activity.clients?.some(
          (client) => {
            if (!client.reports?.length) return true;

            const lastReport = client.reports[client.reports.length - 1];
            return !unableToStartEvents.includes(lastReport.event);
          },
        );

        return (
          !hasFinished &&
          (hasClientWithoutUnableToStart ||
            (activity?.clients ?? []).length == 0) &&
          props.started === hasStarted
        );
      });
    });

    const actionHandlers = {
      started: `rotaactivities/getInProgressActivities`,
      today: `rotaactivities/getActivitiesForUser`,
      retry: `rotaactivities/getActivitiesWithRetry`,
    };

    const actionIcons = {
      started: "clipboard-check",
      today: "clipboard-list",
      retry: "clipboard-question",
    };

    const resolveAction = () => {
      const keys = Object.keys(actionHandlers);
      const key = keys.find((k) => get(props, k, false));
      return get(actionHandlers, key, false);
    };

    const actionIcon = computed(() => {
      const key = Object.keys(actionIcons).find((k) => props[k]);
      return actionIcons[key] || "clipboard-list";
    });

    const today = new Date();
    function inRange(activity) {
      const { startDate } = activity;
      if (props.today) {
        return isToday(startDate);
      }
      return (
        isAfter(startDate, startOfDay(subDays(today, 30))) &&
        isBefore(startDate, endOfDay(subDays(today, 1)))
      );
    }

    const action = resolveAction();
    if (action !== false) {
      store
        .dispatch(action, {
          startDate: startOfDay(today),
          endDate: endOfDay(today),
          includeUnassigned: "false",
        })
        .then((data) => {
          if (data !== false) {
            activities.value = data;
          }
        });
    }

    let activityListenerCancel;

    /*
      If we receive an activity update, see if we need to update our list of activities
    */
    onBeforeUnmount(() => {
      activityListenerCancel();
    });
    onBeforeMount(() => {
      activityListenerCancel = Hub.listen("activity", ({ payload }) => {
        switch (payload.event) {
          case "created":
            if (inRange(payload.data)) {
              if (activities.value === false) {
                activities.value = [payload.data];
              } else {
                activities.value.push(payload.data);
              }
            }
            break;
          case "updated":
            // eslint-disable-next-line no-case-declarations
            const i = activities.value.findIndex(
              (activity) => activity.id === payload.data.id,
            );
            if (i === -1) {
              if (inRange(payload.data)) {
                activities.value.push(payload.data);
              }
            } else {
              activities.value[i] = payload.data;
            }
            break;
          default:
        }
      });
    });

    return {
      myActivities,
      onClick(activity) {
        showViewActivityPopup({ rotaActivity: activity });
      },
      actionIcon,
    };
  },
};
</script>
